import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ProfileInfo from './components/profile-info';
import ProjectSelectionScreen from '../components/ProjectSelectionScreen';
import ProjectGateHeader from '../components/ProjectGateHeader';
import ContentLoader from '../components/ContentLoader';
import { loadProjectState } from '../services/clientProjectData';
import { clearCacheOnProjectChange } from '../Common/menuCacheUtils';
import { useAuth } from '../auth/AuthContext';
import { isAdminUser } from '../auth/roleUtils';
import { useAppTheme } from '../hooks/useAppTheme';
type ProgressLabel = 'Not started' | 'In progress' | 'Completed' | 'Error';

const Projects = () => {
  useAppTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [projectState, setProjectState] = useState<{
    data?: { projectId: number; state: number }[];
  } | null>(null);

  useEffect(() => {
    if (user && isAdminUser(user)) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchProjectData = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const projectList = user.projectDetails?.map((obj) => obj.projectId) ?? [];
        const projectInfo = await loadProjectState({
          userId: user.userId,
          projectId: projectList,
        });
        setProjectState(projectInfo);
      } catch (error) {
        console.error('Error fetching project data:', error);
        setProjectState(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectData();
  }, [user]);

  const getProjectProgress = (projectId: number): ProgressLabel => {
    const state = projectState?.data?.find((item) => item.projectId === projectId);
    if (!state) {
      return projectState ? 'Error' : 'Not started';
    }
    if (state.state === 0) return 'Not started';
    if (state.state >= 1 && state.state <= 2) return 'In progress';
    return 'Completed';
  };

  const handleSelect = (projectId: number | string) => {
    localStorage.setItem('projectId', String(projectId));
    clearCacheOnProjectChange();
    navigate('/dashboard');
  };

  const projects = useMemo(
    () =>
      (user?.projectDetails ?? []).map((project) => ({
        id: project.projectId,
        name: project.projectName || 'Untitled project',
        companyName: project.companyName,
        status: getProjectProgress(project.projectId),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.projectDetails, projectState]
  );

  if (isLoading) {
    return (
      <div className="ps-gate app-page-background">
        <ProjectGateHeader actions={<ProfileInfo />} />
        <main className="ps-gate__main ps-gate__main--centered">
          <ContentLoader page label="Loading projects…" />
        </main>
      </div>
    );
  }

  return (
    <ProjectSelectionScreen
      title="Your projects"
      subtitle="Select a project to start your assessment workspace."
      projects={projects}
      onSelect={handleSelect}
      profileSlot={<ProfileInfo />}
    />
  );
};

export default Projects;
